import { Check, ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/utils';
import { fieldFocus, fieldSize, fieldSurface } from './field-surface';

export function SelectPopover({
	value,
	onChange,
	options,
	placeholder = 'Select…',
}: {
	value: string;
	onChange: (v: string) => void;
	options: Array<{ value: string; label: string }>;
	placeholder?: string;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');

	const filtered = useMemo(() => {
		if (!query) return options;
		const q = query.toLowerCase();
		return options.filter((o) => o.label.toLowerCase().includes(q));
	}, [options, query]);

	const selectedLabel = options.find((o) => o.value === value)?.label;

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setQuery('');
			}}
		>
			<PopoverTrigger
				className={cn(
					fieldSurface,
					fieldSize,
					fieldFocus,
					'flex w-full cursor-pointer items-center justify-between outline-none hover:border-line-3',
					!value && 'text-muted-foreground'
				)}
			>
				<span className="truncate">{selectedLabel || placeholder}</span>
				<ChevronDown size={14} className="shrink-0 text-muted-foreground" />
			</PopoverTrigger>
			<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
				<div className="border-b border-line-1 p-2">
					<div className="relative">
						<Search
							size={12}
							className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-4"
						/>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search…"
							className="h-7 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-8 pr-2 text-xs text-text-1 outline-none placeholder:text-text-4"
							autoFocus
						/>
					</div>
				</div>
				<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
					{filtered.length === 0 ? (
						<div className="px-2 py-3 text-center text-sm text-text-4">No results</div>
					) : (
						filtered.map((opt) => (
							<button
								key={opt.value}
								onClick={() => {
									onChange(opt.value);
									setOpen(false);
									setQuery('');
								}}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
									value === opt.value
										? 'bg-surface-fill text-text-1'
										: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
								)}
							>
								<Check
									size={12}
									className={cn(
										'shrink-0',
										value === opt.value ? 'text-accent' : 'invisible'
									)}
								/>
								{opt.label}
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
